import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DataSnapshot } from "../lib/types";
import { loadSkillGraph } from "../lib/api";
import { queryKeys } from "../lib/query-client";
import { BrandRail } from "../components/BrandRail";
import { FreshnessBar } from "../components/FreshnessBar";
import { KnowledgeGraphExplorer } from "../components/KnowledgeGraphExplorer";
import { ResearchLoopPanel } from "../components/ResearchLoopPanel";

interface Props {
  data: DataSnapshot;
}

export function Graph({ data }: Props) {
  const { stats, meta } = data;
  const [selected, setSelected] = useState<string | null>(null);
  const [showLoop, setShowLoop] = useState(false);

  const { data: graph, isLoading, error } = useQuery({
    queryKey: queryKeys.skillGraph,
    queryFn: loadSkillGraph,
  });

  const issueCount = (stats.statusCounts?.warning ?? 0) + (stats.statusCounts?.critical ?? 0);

  return (
    <div className="graph-grid grid min-h-screen" style={{ gridTemplateColumns: "220px 1fr" }}>
      <BrandRail counts={{ monitor: stats.total, issues: issueCount }} />

      <main className="min-w-0 flex flex-col">
        <FreshnessBar meta={meta} />
        <header className="px-6 pt-6 pb-4 border-b border-fg">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
              // Knowledge graph · clusters · explore connections
            </span>
            {graph && (
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
                {graph.nodeCount} skills · {graph.edgeCount} links · {Object.keys(graph.neuralCommunities ?? {}).length} clusters
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <h1 className="font-display font-extrabold leading-[0.86] tracking-[-0.045em] text-t-72">
              Graph
            </h1>
            <div className="flex flex-col gap-1 pb-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted">
                Explore skill relationships
              </span>
              <span className="font-mono text-[12px] uppercase tracking-[0.12em] text-muted">
                {selected ? `Selected: ${selected}` : "No selection"}
              </span>
            </div>
            {graph?.researchLoop && (
              <button
                type="button"
                onClick={() => setShowLoop((v) => !v)}
                className="ml-auto self-end font-mono text-[10px] uppercase tracking-[0.12em] px-3 py-2 border border-fg hover:bg-fg hover:text-bg font-medium"
              >
                {showLoop ? "Hide" : "Show"} research loop
              </button>
            )}
          </div>

          <div className="editorial-rule mt-4" />
        </header>

        {isLoading && (
          <p className="p-6 font-mono text-[12px] uppercase text-muted">// loading skill-graph.json</p>
        )}
        {error && (
          <p className="p-6 font-mono text-[12px] text-bad">
            // {error instanceof Error ? error.message : "graph load failed"} — run npm run build:data
          </p>
        )}

        {showLoop && graph?.researchLoop && <ResearchLoopPanel loop={graph.researchLoop} />}

        {graph && (
          <KnowledgeGraphExplorer
            graph={graph}
            selected={selected}
            onSelect={setSelected}
          />
        )}
      </main>
    </div>
  );
}
